import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Table, Thead, Tbody, Tr, Th, Td, Link,
  Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiExternalLink } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function Ingresses() {
  const [ingresses, setIngresses] = useState([]);
  const [namespace, setNamespace] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIngresses = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/k8s/ingresses/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setIngresses(data.ingresses || []);
        setNamespace(data.namespace || '');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchIngresses(); }, [fetchIngresses]);

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Ingresses</Heading>
          <Text color="gray.500">
            {ingresses.length} ingresses in namespace <Badge colorScheme="blue">{namespace}</Badge>
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchIngresses}>Refresh</Button>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Host</Th>
              <Th>Path</Th>
              <Th>Service</Th>
              <Th>Port</Th>
              <Th>TLS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {ingresses.map((ing) =>
              (ing.rules || []).map((rule, ri) =>
                (rule.paths || [{ path: '/', service: '—', port: '—' }]).map((p, pi) => (
                  <Tr key={`${ing.name}-${ri}-${pi}`} _hover={{ bg: 'gray.50' }}>
                    {pi === 0 && ri === 0 && (
                      <Td rowSpan={(ing.rules || []).reduce((s, r) => s + (r.paths || []).length, 0)}>
                        <Text fontWeight="medium" fontSize="sm">{ing.name}</Text>
                      </Td>
                    )}
                    {pi === 0 && (
                      <Td rowSpan={(rule.paths || []).length}>
                        <Link href={`https://${rule.host}`} isExternal color="blue.500" fontSize="sm">
                          {rule.host} <FiExternalLink style={{ display: 'inline' }} />
                        </Link>
                      </Td>
                    )}
                    <Td><Text fontSize="sm">{p.path}</Text></Td>
                    <Td><Badge colorScheme="purple" fontSize="xs">{p.service}</Badge></Td>
                    <Td><Text fontSize="sm">{p.port}</Text></Td>
                    {pi === 0 && ri === 0 && (
                      <Td rowSpan={(ing.rules || []).reduce((s, r) => s + (r.paths || []).length, 0)}>
                        {(ing.tls || []).length > 0 ? (
                          <Badge colorScheme="green">TLS</Badge>
                        ) : (
                          <Badge colorScheme="gray">None</Badge>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))
              )
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default Ingresses;
